import { Recycle, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { api } from "@/api";
import { formatError } from "@/auth";
import { TagGroups } from "@/components/PipelineGroups";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import "@/leadrecycle.css";
import { DateField } from "@/components/DateField";

const STATUS = { todo: "À faire", standby: "En attente", lost: "Perdu", cancelled: "Annulé / sans suite", won: "Gagné" };
const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const ago = (m) => { const d = new Date(); d.setMonth(d.getMonth() - m); return d.toISOString().slice(0, 10); };
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "—");
const toggleIn = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

function CheckList({ items, selected, onChange, testid }) {
  const [q, setQ] = useState("");
  const [all, setAll] = useState(false);
  // Sans ascenseur interne : cochés d'abord, 10 lignes, puis « Voir tout »
  const match = items.filter((it) => it.label.toLowerCase().includes(q.toLowerCase())).sort((a, b) => selected.includes(b.id) - selected.includes(a.id));
  const limit = all ? 400 : q ? 30 : 10;
  const shown = match.slice(0, limit);
  return (
    <div className="lr-check" data-testid={testid}>
      <input placeholder="Rechercher…" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testid}-search`} />
      <div className="lr-check-list">
        {shown.map((it) => <label key={it.id}><input type="checkbox" checked={selected.includes(it.id)} onChange={() => onChange(toggleIn(selected, it.id))} data-testid={`${testid}-opt-${it.id}`} /><span>{it.label}</span></label>)}
      </div>
      {match.length > shown.length && <button type="button" className="link-button" onClick={() => setAll(true)} data-testid={`${testid}-more`}>Voir tout ({match.length})</button>}
      {all && match.length > 10 && <button type="button" className="link-button" onClick={() => setAll(false)} data-testid={`${testid}-less`}>Réduire</button>}
      {selected.length > 0 && <button type="button" className="link-button" onClick={() => onChange([])} data-testid={`${testid}-clear`}>Tout décocher ({selected.length})</button>}
    </div>
  );
}

function Filters({ opts, f, setF, onSearch, loading, onGroupsChanged }) {
  const tagItems = opts.tags.map((t) => ({ id: t, label: t }));
  const stepItems = opts.steps.map((s) => ({ id: s.id, label: `${s.pipeline} › ${s.name} (${s.count})` }));
  const set = (k) => (v) => setF({ ...f, [k]: v });
  return (
    <section className="settings-section lr-filters" data-testid="lr-filters">
      <div><h4>Tags inclus</h4><TagGroups prefix="lr-include-group" groups={opts.tag_groups} allTags={opts.tags} selected={f.include_tags} onSelect={set("include_tags")} onChanged={onGroupsChanged} /><CheckList items={tagItems} selected={f.include_tags} onChange={set("include_tags")} testid="lr-include-tags" /></div>
      <div><h4>Tags exclus</h4><TagGroups prefix="lr-exclude-group" groups={opts.tag_groups} allTags={opts.tags} selected={f.exclude_tags} onSelect={set("exclude_tags")} onChanged={onGroupsChanged} /><CheckList items={tagItems} selected={f.exclude_tags} onChange={set("exclude_tags")} testid="lr-exclude-tags" /></div>
      <div><h4>Étapes</h4><CheckList items={stepItems} selected={f.step_ids} onChange={set("step_ids")} testid="lr-steps" /></div>
      <div>
        <h4>Statut</h4>
        <div className="lr-chips">{opts.statuses.map((s) => <button key={s} type="button" className={f.statuses.includes(s) ? "is-on" : ""} onClick={() => set("statuses")(toggleIn(f.statuses, s))} data-testid={`lr-status-${s}`}>{STATUS[s] || s}</button>)}</div>
        <h4>Motif de perte</h4>
        <div className="lr-chips" data-testid="lr-lost-reasons">
          {["Pas intéressé", ...(opts.lost_reasons || []).filter((r) => r !== "Pas intéressé")].map((r) => <button key={r} type="button" className={f.lost_reason === r ? "is-on" : ""} onClick={() => setF({ ...f, lost_reason: f.lost_reason === r ? null : r, ...(f.lost_reason === r ? { lost_before: null } : {}) })} data-testid={`lr-lost-reason-${r.replace(/\W+/g, "-")}`}>{r}</button>)}
        </div>
        {f.lost_reason && <><h4>Perdue depuis plus de</h4><div className="lr-chips">{[1, 3, 6, 12].map((m) => <button key={m} type="button" className={f.lost_before === ago(m) ? "is-on" : ""} onClick={() => set("lost_before")(f.lost_before === ago(m) ? null : ago(m))} data-testid={`lr-lost-age-${m}`}>{m} mois</button>)}</div></>}
        <h4>Sans activité depuis</h4>
        <div className="lr-chips">{[3, 6, 12].map((m) => <button key={m} type="button" className={f.inactive_before === ago(m) ? "is-on" : ""} onClick={() => set("inactive_before")(f.inactive_before === ago(m) ? null : ago(m))} data-testid={`lr-age-${m}`}>{m} mois</button>)}</div>
        <label className="lr-date">ou avant le <DateField value={f.inactive_before || ""} onChange={(v) => set("inactive_before")(v || null)} testid="lr-age-date" /></label>
        <label className="lr-skip"><input type="checkbox" checked={f.skip_recent_days > 0} onChange={(e) => set("skip_recent_days")(e.target.checked ? 90 : 0)} data-testid="lr-skip-recent" />Exclure les leads déjà recyclés ces 90 derniers jours</label>
        <button type="button" className="primary-button" onClick={onSearch} disabled={loading} data-testid="lr-search"><Search size={15} />{loading ? "Recherche…" : "Rechercher les leads"}</button>
      </div>
    </section>
  );
}

function TagChips({ tags, id }) {
  const rest = tags.length - 3;
  return (
    <div className="lr-chiplist" data-testid={`lr-row-tags-${id}`}>
      {tags.slice(0, 3).map((t) => <span key={t} className="lr-tagchip" title={t}>{t}</span>)}
      {rest > 0 && <span className="lr-tagchip lr-tagmore" title={tags.join("\n")} data-testid={`lr-row-tags-more-${id}`}>+{rest}</span>}
    </div>
  );
}

function Preview({ res, limit, setLimit, off, setOff }) {
  const take = res.leads.slice(0, limit);
  const [rows, setRows] = useState(50);
  return (
    <section className="settings-section" data-testid="lr-preview">
      <div className="lr-preview-head">
        <h3 data-testid="lr-total">{res.total.toLocaleString("fr-FR")} lead(s) trouvé(s)</h3>
        <label>Limite <input type="number" min={1} max={5000} value={limit} onChange={(e) => setLimit(Math.max(1, Number(e.target.value) || 1))} data-testid="lr-limit" /> <small>les plus anciens d'abord</small></label>
        <strong data-testid="lr-selected">{take.filter((l) => !off.includes(l.lead_id)).length} sélectionné(s)</strong>
      </div>
      <div className="lr-table-wrap">
        <table className="agent-table lr-table">
          <thead><tr><th></th><th>Nom</th><th>Ville</th><th>Tags</th><th>Étape</th><th>Agent d'origine</th><th>Dernière activité</th></tr></thead>
          <tbody>
            {take.slice(0, rows).map((l) => (
              <tr key={l.lead_id} className={off.includes(l.lead_id) ? "lr-off" : ""} data-testid={`lr-row-${l.lead_id}`}>
                <td><input type="checkbox" checked={!off.includes(l.lead_id)} onChange={() => setOff(toggleIn(off, l.lead_id))} data-testid={`lr-row-check-${l.lead_id}`} /></td>
                <td>{l.title}</td><td data-testid={`lr-row-city-${l.lead_id}`}>{l.city || "—"}</td><td className="lr-tags"><TagChips tags={l.tags} id={l.lead_id} /></td><td>{l.pipeline} › {l.step} <small>({STATUS[l.status] || l.status})</small></td><td>{l.owner_name || "—"}</td><td>{fmt(l.last_activity)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {take.length > rows && <button type="button" className="link-button lr-more-rows" onClick={() => setRows(rows + 50)} data-testid="lr-more-rows">Afficher 50 lignes de plus ({take.length - rows} restantes)</button>}
    </section>
  );
}

function Destination({ opts, d, setD, count, onRun }) {
  const pipelines = [...new Set(opts.steps.map((s) => s.pipeline))];
  const [pipe, setPipe] = useState("");
  return (
    <section className="settings-section lr-dest" data-testid="lr-destination">
      <div><h4>Agent(s) qui reçoivent le lot <small>(parts égales)</small></h4><CheckList items={opts.users.map((u) => ({ id: u.user_id, label: u.name }))} selected={d.agents} onChange={(v) => setD({ ...d, agents: v })} testid="lr-agents" /></div>
      <div>
        <h4>Tag ajouté au lot</h4><input value={d.tag} onChange={(e) => setD({ ...d, tag: e.target.value })} data-testid="lr-tag" />
        <h4>Pipeline et étape de destination</h4>
        <select value={pipe} onChange={(e) => { setPipe(e.target.value); setD({ ...d, step: null }); }} data-testid="lr-dest-pipeline"><option value="">Choisir une pipeline…</option>{pipelines.map((p) => <option key={p}>{p}</option>)}</select>
        <select value={d.step || ""} disabled={!pipe} onChange={(e) => setD({ ...d, step: Number(e.target.value) || null })} data-testid="lr-dest-step"><option value="">Choisir une étape…</option>{opts.steps.filter((s) => s.pipeline === pipe).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
        <p className="ck-muted">Chaque lead garde son historique : il est réattribué, passe en « À faire » dans l'étape choisie, reçoit le tag et un commentaire « Recyclé le … ».</p>
        <button type="button" className="primary-button" disabled={!count || !d.agents.length || !d.step || !d.tag.trim()} onClick={onRun} data-testid="lr-run"><Recycle size={15} />Recycler {count} lead(s)</button>
      </div>
    </section>
  );
}

function Jobs({ jobs }) {
  const live = jobs.find((j) => j.status === "running" || j.status === "paused");
  return (
    <section className="settings-section" data-testid="lr-history">
      {live && (
        <div className="lr-live" data-testid="lr-progress">
          <div className="cs-progress"><span style={{ width: `${Math.round((100 * live.done) / live.count)}%` }} /><b>{live.status === "paused" ? "En pause : quota noCRM atteint, reprise automatique · " : ""}{live.done}/{live.count} · {live.ok} réussis · {live.failed} échecs</b></div>
        </div>
      )}
      <h3>Historique des recyclages</h3>
      {jobs.length === 0 ? <p className="ck-muted">Aucun recyclage pour l'instant.</p> : (
        <table className="agent-table">
          <thead><tr><th>Date</th><th>Par</th><th>Leads</th><th>Réussis / échecs</th><th>Agent(s)</th><th>Tag</th><th>Destination</th><th>Filtres</th></tr></thead>
          <tbody>{jobs.map((j) => (
            <tr key={j.id} data-testid={`lr-job-${j.id}`}>
              <td>{new Date(j.created_at).toLocaleString("fr-FR")}</td><td>{j.by}</td><td>{j.count}</td>
              <td>{j.ok} / {j.failed}{j.errors?.length > 0 && <details><summary>voir</summary>{j.errors.map((e) => <div key={e.lead_id}>{e.lead_id} : {e.error}</div>)}</details>}</td>
              <td>{j.agents.map((a) => a.name).join(", ")}</td><td>{j.tag}</td><td>{j.pipeline} › {j.step}</td>
              <td className="lr-tags">{[j.filters.include_tags?.length && `tags : ${j.filters.include_tags.join(", ")}`, j.filters.exclude_tags?.length && `sauf : ${j.filters.exclude_tags.join(", ")}`, j.filters.statuses?.length && `statut : ${j.filters.statuses.map((s) => STATUS[s] || s).join(", ")}`, j.filters.inactive_before && `inactifs avant le ${fmt(j.filters.inactive_before)}`].filter(Boolean).join(" · ") || "—"}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </section>
  );
}

export default function LeadRecyclePage() {
  const now = new Date();
  const [opts, setOpts] = useState({ tags: [], steps: [], users: [], statuses: [], tag_groups: [] });
  const [f, setF] = useState({ include_tags: [], exclude_tags: [], statuses: ["lost", "cancelled"], step_ids: [], inactive_before: ago(6), skip_recent_days: 90 });
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(200);
  const [off, setOff] = useState([]);
  const [d, setD] = useState({ agents: [], tag: `Recyclage ${MONTHS[now.getMonth()]} ${now.getFullYear()}`, step: null });
  const [confirm, setConfirm] = useState(false);
  const [jobs, setJobs] = useState([]);
  const loadJobs = useCallback(() => api.get("/lead-recycle/jobs").then(({ data }) => setJobs(data)).catch(() => {}), []);
  const loadOpts = useCallback(() => api.get("/lead-recycle/options").then(({ data }) => setOpts(data)).catch(() => {}), []);
  useEffect(() => { loadOpts(); loadJobs(); }, [loadOpts, loadJobs]);
  const running = jobs.some((j) => j.status === "running" || j.status === "paused");
  useEffect(() => { if (!running) return undefined; const t = setInterval(loadJobs, 3000); return () => clearInterval(t); }, [running, loadJobs]);
  const search = () => { setLoading(true); setOff([]); api.post("/lead-recycle/preview", f).then(({ data }) => setRes(data)).catch((e) => toast.error(formatError(e.response?.data?.detail))).finally(() => setLoading(false)); };
  const ids = useMemo(() => (res ? res.leads.slice(0, limit).map((l) => l.lead_id).filter((id) => !off.includes(id)) : []), [res, limit, off]);
  const step = opts.steps.find((s) => s.id === d.step);
  const run = () => api.post("/lead-recycle/run", { lead_ids: ids, agent_ids: d.agents, step_id: d.step, tag: d.tag, filters: f })
    .then(() => { toast.success("Recyclage lancé : envoi progressif vers noCRM."); setConfirm(false); setRes(null); loadJobs(); })
    .catch((e) => toast.error(formatError(e.response?.data?.detail)));
  return (
    <section className="page" data-testid="lead-recycle-page">
      <div className="page-heading"><div><p className="eyebrow">Fichiers de prospection</p><h1>Recyclage de leads</h1><p className="pl-muted">Retrouvez d'anciens leads noCRM, choisissez-en un lot et remettez-les en prospection sans les dupliquer.</p></div></div>
      <Filters opts={opts} f={f} setF={setF} onSearch={search} loading={loading} onGroupsChanged={loadOpts} />
      {res && <Preview res={res} limit={limit} setLimit={setLimit} off={off} setOff={setOff} />}
      {res && <Destination opts={opts} d={d} setD={setD} count={ids.length} onRun={() => setConfirm(true)} />}
      <Jobs jobs={jobs} />
      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent data-testid="lr-confirm">
          <DialogHeader><DialogTitle>Confirmer le recyclage</DialogTitle></DialogHeader>
          <ul className="lr-recap">
            <li><b>{ids.length}</b> lead(s), envoyés progressivement (3 requêtes noCRM par lead, soit ≈ {ids.length * 3} sur le quota de 9 000/jour)</li>
            <li>Agent(s) : <b>{opts.users.filter((u) => d.agents.includes(u.user_id)).map((u) => u.name).join(", ")}</b>{d.agents.length > 1 ? " (parts égales)" : ""}</li>
            <li>Tag ajouté : <b>{d.tag}</b></li>
            <li>Destination : <b>{step ? `${step.pipeline} › ${step.name}` : "—"}</b>, statut « À faire »</li>
          </ul>
          <DialogFooter>
            <button type="button" className="secondary-button" onClick={() => setConfirm(false)} data-testid="lr-confirm-cancel">Annuler</button>
            <button type="button" className="primary-button" onClick={run} data-testid="lr-confirm-run">Lancer le recyclage</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
